import { ArrowLeftCircleIcon, ArrowRightCircleIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { TextMessageThread } from 'pages/Loan/Servicing/TextMessages/TextMessageThread'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getLoanTextMessagesUnread, streamLoanTextMessages } from 'services'
import Api from 'services/api'
import { getOpenLoanDisplayNo } from 'utils'

import { floaterPanelEdgeClass, floaterTabEdgeClass, FloaterTabWrapper, useFloaterTab } from './useFloaterTab'

/** Fallback only; the badge normally updates from the live stream. */
const UNREAD_POLL_INTERVAL = 60000

/**
 * Text Messages floater: the borrower SMS thread, reachable from any loan screen, with a badge on
 * the tab when the borrower has replied and nobody has opened the thread yet.
 */
export const TextMessagesSlide = () => {
  const loanDetail = useSelector((state: any) => state.loanDetail)
  const [isShowSlide, setShowSlide] = useState(false)
  const [unread, setUnread] = useState(0)
  const floater = useFloaterTab('textMessages', { side: 'right', percent: 25, offset: 120, mobileTop: 416 })

  useEffect(() => {
    // Opening the thread marks it read, so stop counting while the panel is up.
    if (isShowSlide) {
      setUnread(0)
      return
    }
    const check = () => {
      if (document.hidden) return
      getLoanTextMessagesUnread()
        .then((res) => setUnread(res?.count || 0))
        .catch(() => {})
    }
    check()
    // A reply lights the badge immediately; the interval is only there if the stream is down.
    const stopStream = streamLoanTextMessages(Api.loanNumber, { onActivity: check })
    const timer = setInterval(check, UNREAD_POLL_INTERVAL)
    document.addEventListener('visibilitychange', check)
    return () => {
      stopStream()
      clearInterval(timer)
      document.removeEventListener('visibilitychange', check)
    }
  }, [isShowSlide, loanDetail?.loanNumber])

  const onShowSlide = () => setShowSlide(!isShowSlide)

  return (
    <>
      <FloaterTabWrapper floater={floater}>
        <button
          className={`p-3 ${floaterTabEdgeClass(
            floater.side,
          )} ring-[1px] ring-shade-blue bg-shade-blue text-white border hover:bg-white border-shade-blue hover:text-shade-blue rounded flex items-center gap-2 w-auto sm:w-[155px]`}
          {...floater.tabProps(onShowSlide)}
        >
          <ChatBubbleLeftRightIcon className="w-6 h-6" />
          <span className="hidden sm:inline">Text</span>
          {unread > 0 && (
            <span className="ml-auto bg-red-600 text-white text-xs font-bold rounded-full px-2 py-0.5">{unread}</span>
          )}
        </button>
      </FloaterTabWrapper>
      {isShowSlide && (
        <div
          className={`shadow1 fixed bg-white border z-50 top-[5vh] bottom-0 w-[32rem] max-w-full h-[90vh] overflow-hidden ease-in-out ${floaterPanelEdgeClass(
            floater.side,
          )}`}
        >
          <div className="w-[32rem] max-w-full fixed z-20 rounded-t flex justify-between p-3 font-bold text-sm place-items-center items-center text-shade-blue border-b bg-white">
            <div className="flex items-center">
              <p className="text-lg text-gray-800">Text Messages: </p>
              <span className="text-[16px] ml-2 italic font-semibold">
                {getOpenLoanDisplayNo(loanDetail)}
              </span>
            </div>

            <button className="hover:underline hover-shadow1 p-1 rounded" onClick={onShowSlide}>
              {floater.side === 'left' ? (
                <ArrowLeftCircleIcon className="w-5 h-5" />
              ) : (
                <ArrowRightCircleIcon className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="pt-14 h-full overflow-auto relative text-sm text-gray-900 p-3">
            <TextMessageThread compact />
          </div>
        </div>
      )}
    </>
  )
}
